import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * Tooltip component with an info icon that shows a message on hover/tap.
 */
const Tooltip = ({
    content,
    position = 'top',
    children,
    icon = 'Info',
    size = 14,
    className = ""
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef(null);

    // Toggle for mobile (tap)
    const toggleTooltip = (e) => {
        e.stopPropagation();
        setIsVisible(!isVisible);
    };

    // Close when clicking outside
    useEffect(() => {
        if (!isVisible) return;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isVisible]);

    // Handle position styles
    const getPositionStyles = () => {
        switch (position) {
            case 'bottom':
                return {
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '8px'
                };
            case 'left':
                return {
                    top: '50%',
                    right: '100%',
                    transform: 'translateY(-50%)',
                    marginRight: '8px'
                };
            case 'right':
                return {
                    top: '50%',
                    left: '100%',
                    transform: 'translateY(-50%)',
                    marginLeft: '8px'
                };
            case 'top':
            default:
                return {
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '8px'
                };
        }
    };

    // Handle arrow styles
    const getArrowStyles = () => {
        switch (position) {
            case 'bottom':
                return {
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderBottom: '6px solid var(--color-popover, #1f2937)',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent'
                };
            case 'left':
                return {
                    left: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderLeft: '6px solid var(--color-popover, #1f2937)',
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent'
                };
            case 'right':
                return {
                    right: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    borderRight: '6px solid var(--color-popover, #1f2937)',
                    borderTop: '6px solid transparent',
                    borderBottom: '6px solid transparent'
                };
            case 'top':
            default:
                return {
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    borderTop: '6px solid var(--color-popover, #1f2937)',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent'
                };
        }
    };

    return (
        <div
            ref={containerRef}
            className={`inline-flex items-center relative group ${className}`}
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={() => setIsVisible(false)}
            onClick={toggleTooltip}
        >
            {children ? children : (
                <span className="p-0.5 cursor-help opacity-60 hover:opacity-100 transition-opacity">
                    <Icon name={icon} size={size} />
                </span>
            )}

            {isVisible && (
                <div
                    className="absolute z-[3000] px-3 py-2 text-xs font-medium text-popover-foreground rounded-lg shadow-xl pointer-events-auto animate-in fade-in zoom-in duration-200"
                    style={{
                        ...getPositionStyles(),
                        backgroundColor: 'var(--color-popover)',
                        width: 'max-content',
                        maxWidth: '220px',
                        lineHeight: '1.4',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    {content}
                    <div
                        className="absolute"
                        style={getArrowStyles()}
                    />
                </div>
            )}
        </div>
    );
};

export default Tooltip;
